import makeStyles from '@mui/styles/makeStyles'

export const useStyles = makeStyles(
  ({ palette, typography, spacing, breakpoints, radius, customShadows }) => ({
    // MT.3 — PlaylistCard reads as a collection. At rest, all three
    // layers (front Card + two pseudo-element ghosts) stack at the same
    // (0, 0) position so the card looks like a single surface. On hover
    // the front Card and the middle ghost slide up-left, revealing the
    // back ghost which stays anchored at (0, 0). The parent general-card
    // `:hover { filter: drop-shadow }` is suppressed locally so the back
    // ghost doesn't pick up a shadow that would read as movement.
    root: {
      maxWidth: typography.pxToRem(384),
      position: 'relative',
      // Local stacking context so the pseudo-elements with `z-index: -1/-2`
      // sit behind the front Card *within this Box* — without `isolation`
      // they'd paint at the document's negative z-level and get hidden
      // behind page/Variant background.
      isolation: 'isolate',
      // `&&:hover` doubles specificity to win over general-card's `.root:hover`.
      '&&:hover': {
        filter: 'none'
      },
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: radius['2xl'],
        background: palette.background.paper,
        boxShadow: customShadows.card,
        pointerEvents: 'none',
        transition: 'transform 0.25s ease-out',
        willChange: 'transform',
        transform: 'translate(0, 0)'
      },
      '&::before': { zIndex: -1 },
      '&::after': { zIndex: -2 },
      // Hover state: ::after stays at (0, 0) — it's the anchor. Middle
      // ghost peeks out a little, front Card moves further up-left so
      // the stack opens diagonally.
      '&:hover::before': {
        transform: `translate(-${spacing(0.5)}, -${spacing(0.5)})`
      },
      '&:hover $card': {
        transform: `translate(-${spacing(1)}, -${spacing(1)})`
      }
    },
    card: {
      paddingBottom: 0,
      transform: 'translate(0, 0)',
      transition: 'transform 0.25s ease-out',
      willChange: 'transform'
    },
    // typography.heading.h4 ships lineHeight: 1, which combined with the
    // multiNoWrap `overflow: hidden` from general-card clips descenders
    // (g/y/p) at the bottom of the title box. `&&` bumps specificity so
    // the line-height wins over the base title class without !important.
    title: ({ isDesktop }) => ({
      '&&': {
        ...(isDesktop && typography.heading.h4),
        ...(isDesktop && { lineHeight: 1.3 })
      }
    }),
    image: {
      height: spacing(24.5)
    },
    progress: {
      height: spacing(1.125),
      borderRadius: radius.lg
    },
    foreground: {
      borderRadius: radius.lg,
      backgroundColor: palette.primary.shade[80]
    },
    background: {
      backgroundColor: palette.grey.shade[10]
    },
    avatar: {
      height: spacing(2.625),
      width: spacing(2.625)
    },
    time: {
      ...typography.body.s,
      color: palette.grey.shade[80],
      display: 'flex',
      alignItems: 'center',
      // Push the "N Items · X mins" group to the right of its Grid cell.
      // Typography has `display='flex'` set by the playlist branch in
      // general-card.js, which overrides the `align='right'` prop —
      // flex children pack to flex-start by default, leaving the label
      // center-ish in the row. flex-end restores right alignment and
      // creates proper spacing from the left-side "Private" pill.
      justifyContent: 'flex-end',
      fontWeight: typography.weight.medium
    },
    // Ensure consistent button heights inside the playlist action row.
    // (Previous `marginLeft: 0 !important` workaround was removed —
    // it patched MUI Grid `spacing` negative margins, but the action
    // row now uses plain Box flex + gap in general-card.js, so there
    // are no negative margins to counter.)
    actionsGrid: {
      '& .MuiButton-root': {
        height: spacing(3.5),
        minHeight: spacing(3.5)
      }
    },
    statusNotStarted: {
      height: spacing(3.5),
      minHeight: spacing(3.5),
      padding: `0 ${spacing(1.25)}`,
      borderRadius: spacing(0.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      backgroundColor: '#FFF3E0',
      color: '#E65100'
    },
    statusInProgress: {
      height: spacing(3.5),
      minHeight: spacing(3.5),
      padding: `0 ${spacing(1.25)}`,
      borderRadius: spacing(0.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      backgroundColor: '#E3F2FD',
      color: '#1565C0'
    },
    statusCompleted: {
      height: spacing(3.5),
      minHeight: spacing(3.5),
      padding: `0 ${spacing(1.25)}`,
      borderRadius: spacing(0.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      backgroundColor: '#E8F5E9',
      color: '#268731'
    },
    // Bottom-right corner square for public playlists
    cornerSquare: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: spacing(5.625),
      height: spacing(5.625),
      backgroundColor: palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      borderRadius: `${radius.lg}px 0 0 0`
    },
    cornerAvatar: {
      width: spacing(3.125),
      height: spacing(3.125),
      marginLeft: spacing(-0.625),
      marginTop: spacing(-0.625)
    }
  })
)
